import { isRouteErrorResponse, useRevalidator, useRouteError } from 'react-router-dom'

import { HTTP_STATUS } from '@ps/common-features/lib/api/constants/httpStatus'
import errorImage from '@ps/ds/assets/illustrations/il-error.svg'
import { ErrorState } from '@ps/ds/components/blocks/ErrorState/ErrorState'

import { NotFoundPage } from '../NotFoundPage/NotFoundPage'

const getHttpStatus = (error: unknown): number | undefined => {
  if (isRouteErrorResponse(error)) return error.status
  if (typeof error === 'object' && error !== null) {
    if ('status' in error && typeof error.status === 'number') return error.status
    if ('statusCode' in error && typeof error.statusCode === 'number') return error.statusCode
    if (
      'response' in error &&
      typeof error.response === 'object' &&
      error.response !== null &&
      'status' in error.response &&
      typeof error.response.status === 'number'
    ) {
      return error.response.status
    }
  }
  return undefined
}

const getDefaultDescription = (status?: number) =>
  status
    ? `An API error (HTTP ${status}) has occurred. Our digital exterminators are on it—please try again!`
    : 'An unexpected application error has occurred. Our digital exterminators are on it—please try again!'

type ErrorPageProps = {
  title?: string
  description?: string
  notFoundTitle?: string
  notFoundDescription?: string
  retryLabel?: string
}

export const ErrorPage = ({
  title = 'Oops! Something’s crawling in the code.',
  description,
  notFoundTitle = 'Page not found',
  notFoundDescription = 'The page you are looking for doesn’t exist or has been moved.',
  retryLabel = 'Retry',
}: ErrorPageProps = {}) => {
  const error = useRouteError()
  const { revalidate } = useRevalidator()
  const status = getHttpStatus(error)

  if (status === HTTP_STATUS.NOT_FOUND) {
    return <NotFoundPage title={notFoundTitle} description={notFoundDescription} />
  }

  return (
    <main className="grid min-h-screen place-content-center p-6">
      <ErrorState
        title={title}
        description={description ?? getDefaultDescription(status)}
        image={<img src={errorImage} alt="" aria-hidden="true" />}
        retryOnClick={revalidate}
        retryLabel={retryLabel}
        variant="fullWidth"
      />
    </main>
  )
}
